import { AfterViewInit, Component, HostListener, NgModuleRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CsModal, Modal, ModalInstance } from '@cargos/cs-modal';
import { Observable, Subject, of, race, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { AuthRedirectService, CurrentUserProfileService } from 'src/app/shared';
import { AcceptanceLegalTermsResult } from 'src/app/shared/modules/acceptance-legal-terms/models/acceptance-legal-terms-result';
import { AcceptanceLegalTermsService } from 'src/app/shared/modules/acceptance-legal-terms/services/acceptance-legal-terms.service';
import { ChromeBoxMessagesService } from 'src/app/shared/services/chrome-box-messages.service';
import { RecaptchaService, SKIP_RECAPTCHA } from 'src/app/shared/services/recaptcha.service';
import { ProfileConst, User } from 'src/app/utils';
import { ReCaptchaEnterpriseAction } from 'src/app/utils/cargo-driver-check-in-model/recaptcha-enterprise';
import { AuthService, LegalTermsService } from 'src/app/utils/cargo-driver-check-in-service/services';
import { ConfirmDispatcherProfileActivationComponent } from './confirm-dispatcher-profile-activation/confirm-dispatcher-profile-activation.component';
import { DeviceUuidService } from './services/device-uuid.service';
import { LoginExceptionService } from './services/login-exception.service';
import { LoginService } from './services/login.service';
import { LoginException } from './types/login-exception';
import { LoginExceptionCode } from './types/login-exception-code.enum';
import { LoginForm } from './types/login-form';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    providers: [
        LoginService,
        LoginExceptionService,
        {
            provide: SKIP_RECAPTCHA,
            useFactory: (svc: ChromeBoxMessagesService) => svc.getDeviceUUID().pipe(map(Boolean)),
            deps: [ChromeBoxMessagesService]
        },
        RecaptchaService
    ],
    standalone: false
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

    public form: FormGroup<LoginForm>;
    public sending: boolean = false;
    public exception: LoginException;
    private csModal: CsModal = ModalInstance.generate({});
    public settingsModal: Observable<Modal>;
    public initialized: boolean;
    private unsubscribe$: Subject<void>;
    private currentKeyCombination: string[] = [];

    constructor(
        private authRedirectService: AuthRedirectService,
        private loginService: LoginService,
        private loginExceptionService: LoginExceptionService,
        private currentUserProfileService: CurrentUserProfileService,
        private router: Router,
        private legalTermsService: LegalTermsService,
        private authUserService: AuthService,
        private acceptanceLegalTermsService: AcceptanceLegalTermsService,
        private snackBar: MatSnackBar,
        private deviceUuidService: DeviceUuidService,
        private recaptchaService: RecaptchaService,
        private ngModuleRef?: NgModuleRef<ConfirmDispatcherProfileActivationComponent>,
    ) {
        this.settingsModal = this.csModal.getSettings();
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.initialized = true;
        this.createForm();
        this.subscribeException();
        this.currentUserProfileService.setUserProfile('');
    }

    ngOnDestroy(): void {
        this.csModal.close();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngAfterViewInit(): void {
        this.raceLoginDevice()
            .subscribe(logged => {
                if (logged) {
                    this.authRedirectService.redirect();
                }
            });
    }

    raceLoginDevice(): Observable<boolean> {
        return race(
            this.loginService.loginDevice(),
            this.loginService.onUserLoginAttempt()
                .pipe(
                    map(() => false)
                )
        )
            .pipe(
                take(1)
            );
    }

    @HostListener('document:keypress', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (!event.shiftKey) {
            this.currentKeyCombination = [];
            return;
        }
        this.currentKeyCombination.push(event.key.toLowerCase());
        if (this.currentKeyCombination.join('') != 'y') {
            return;
        }
        this.getUuid();
    }

    getUuid(): void {
        this.deviceUuidService.getDeviceUUID()
            .pipe(
                take(1),
                switchMap(uuid => {
                    if (!uuid) {
                        return throwError(() => 'UUID not found');
                    }
                    return this.openSnackBar(uuid);
                }),
            ).subscribe({
                next: (uuid) => {
                    navigator.clipboard.writeText(uuid);
                    this.currentKeyCombination = [];
                },
                error: error => {
                    this.currentKeyCombination = [];
                    console.error(error);
                }
            });
    }

    openSnackBar(uuid: string): Observable<string> {
        return this.snackBar.open(`Device ID: ${uuid}`, 'Copy')
            .afterDismissed().pipe(map(() => uuid));
    }

    createForm(): void {
        this.form = new FormGroup<LoginForm>({
            usr_email: new FormControl<string>('', [Validators.required, Validators.email]),
            usr_password: new FormControl<string>('', Validators.required),
        });
    }

    fixEmailAutoFill(usr_email: string): void {
        if (!usr_email) {
            return;
        }

        this.form.patchValue({
            usr_email
        });
    }

    subscribeException(): void {
        this.loginExceptionService.getCurrentException()
            .pipe(
                takeUntil(this.unsubscribe$)
            ).subscribe(exception => {
                this.exception = exception;
            });
    }

    confirmDispatcherProfileActivation(): void {
        this.csModal.confirm<ConfirmDispatcherProfileActivationComponent>({
            content: {
                componentClass: ConfirmDispatcherProfileActivationComponent,
                options: {
                    ngModuleRef: this.ngModuleRef
                }
            },
            contentAlign: 'center',
            columnClass: 'medium',
            fontSize: 'medium',
            onContentReady: (modal: Modal<ConfirmDispatcherProfileActivationComponent>) => {
                const contentInstance = modal.contentInstance;

                contentInstance.cancelEvent
                    .pipe(
                        take(1),
                        mergeMap(() =>
                            this.loginService.logout()
                        )
                    )
                    .subscribe(() => {
                        modal.close();
                    });
            },
            closeIcon: true,
            escapeKey: true,
            backgroundDismiss: true
        });
    }

    validateReCaptchaLogin(usr_email: string): Observable<unknown> {
        return this.recaptchaService.execute(ReCaptchaEnterpriseAction.LOGIN)
            .pipe(
                catchError(() => throwError(() => ({ ...RecaptchaService.CONNECTIVITY_ERROR, code: LoginExceptionCode.RECAPTCHA_CONNECTIVITY_ERROR }))),
                switchMap(recaptchaToken =>
                    recaptchaToken
                        ? this.loginService.validateReCaptchaLogin(usr_email, recaptchaToken)
                        : of(null)
                )
            );
    }

    login(): void {
        if (this.form.invalid || this.sending) {
            return;
        }

        this.loginService.emitUserLoginAttempt();
        this.sending = true;

        const { usr_email, usr_password } = this.form.value;

        this.validateReCaptchaLogin(usr_email)
            .pipe(
                switchMap(() =>
                    this.loginService.login(usr_email, usr_password)
                        .pipe(
                            switchMap(user => {
                                if (!user) {
                                    return of(user);
                                }

                                return this.launchLegalTermsIfApplicable(user);
                            })
                        )
                ),
            )
            .subscribe({
                next: (user) => {
                    this.sending = false;
                    if (!user) {
                        this.router.navigateByUrl('sign-up/complete-profile', { replaceUrl: true });
                        return
                    }
                    if (this.authRedirectService.isProfileValid()) {
                        this.authRedirectService.redirect();
                    }

                    if (user.usr_profile === ProfileConst.driver_pf) {
                        this.confirmDispatcherProfileActivation();
                    }
                },
                error: (error: LoginException) => {
                    this.sending = false;
                    this.loginExceptionService.setException(error);
                }
            });
    }

    launchLegalTermsIfApplicable(user: User): Observable<User> {
        return this.acceptanceLegalTermsService.launchIfApplicable()
            .pipe(
                take(1),
                mergeMap(result => {
                    if (result === AcceptanceLegalTermsResult.DECLINED) {
                        this.authUserService.logout();

                        return throwError(() => ({
                            message: 'Accept the legal terms to be able to continue using the platform',
                            code: LoginExceptionCode.PENDING_ACCEPT_LEGAL_TERMS
                        }));
                    }

                    this.acceptanceLegalTermsService.saveFlagRecentLogin();

                    if (result === AcceptanceLegalTermsResult.ACCEPTED) {
                        return this.authUserService.onUserChanges()
                            .pipe(
                                take(1)
                            );
                    }

                    return of(user);
                })
            );
    }
}
